import Clipboard from "@react-native-clipboard/clipboard";
import {
    Alert,
    DataTable,
    Loading,
    PageTitle,
    StyleFunction,
    Surface,
    useAuth,
    useTheme,
    useThemedStyle,
    useToast,
} from "@venuepos/react-common";
import { useFiskalyOrganizationsQuery } from "graphql-sdk";
import { useTranslation } from "locales";
import React, { useCallback } from "react";
import { RootStackScreenProps } from "../../../navigation";
import { AdminContainer } from "../../container";

type ScreenProps = RootStackScreenProps<"FISKALY_ORGANIZATIONS">;

export function FiskalyOrganizationsScreen({
    navigation: { navigate },
}: ScreenProps) {
    const auth = useAuth();
    auth.enforce(["admin.role"]);

    const styles = useThemedStyle(styleFunc);
    const theme = useTheme();
    const [t] = useTranslation();
    const toast = useToast();

    const { data, loading, error } = useFiskalyOrganizationsQuery({
        fetchPolicy: "no-cache",
    });

    const copyOnClick = useCallback(
        (text: string) => {
            Clipboard.setString(text);
            toast.info(t("backoffice.info.text_copied", "Text copied"));
        },
        [t, toast]
    );

    return (
        <AdminContainer>
            <Surface>
                <PageTitle>
                    {t(
                        "backoffice.integration.fiskaly.organization_list",
                        "Fiskaly organizations"
                    )}
                </PageTitle>
                {error ? (
                    <Alert type="error">
                        {t(
                            "backoffice.error.from_server",
                            "There was an error: {{errorText}}",
                            {
                                errorText: error?.message,
                            }
                        )}
                    </Alert>
                ) : (
                    <DataTable>
                        <DataTable.Header>
                            <DataTable.Title>
                                {t(
                                    "backoffice.integration.fiskaly.display_name",
                                    "Display name"
                                )}
                            </DataTable.Title>
                            <DataTable.Title>
                                {t("common.name", "Name")}
                            </DataTable.Title>
                            <DataTable.Title>
                                {t("common.address", "Address")}
                            </DataTable.Title>
                            <DataTable.Title style={styles.zipColumn}>
                                {t("backoffice.common.zip", "Zip")}
                            </DataTable.Title>
                            <DataTable.Title>
                                {t("backoffice.common.city", "City")}
                            </DataTable.Title>
                            <DataTable.Title style={styles.countryColumn}>
                                {t(
                                    "backoffice.common.country_code",
                                    "Country code"
                                )}
                            </DataTable.Title>
                            <DataTable.Title style={styles.envColumn}>
                                {t(
                                    "backoffice.integration.fiskaly.environment",
                                    "Env."
                                )}
                            </DataTable.Title>
                            <DataTable.Title>
                                {`${t(
                                    "backoffice.integration.fiskaly.managed_by",
                                    "Managed by"
                                )}`}
                            </DataTable.Title>
                            <DataTable.Title>
                                {`${t(
                                    "backoffice.common.merchant",
                                    "Merchant"
                                )}`}
                            </DataTable.Title>
                            <DataTable.Title>
                                {`${t("backoffice.common.id", "ID")} (${t(
                                    "backoffice.common.click_to_copy",
                                    "Click to copy"
                                )})`}
                            </DataTable.Title>
                        </DataTable.Header>
                        {loading ? (
                            <Loading />
                        ) : (
                            data.fiskalyOrganizations.data.map(
                                (itr, rowIndex) => {
                                    const parentOrg =
                                        data.fiskalyOrganizations.data.find(
                                            listItr =>
                                                itr.managedBy === listItr.id
                                        );

                                    const parentOrgName =
                                        parentOrg?.displayName ||
                                        parentOrg?.name ||
                                        "";

                                    return (
                                        <DataTable.Row
                                            style={
                                                rowIndex % 2 === 0
                                                    ? theme.styles.oddRow
                                                    : null
                                            }
                                            key={`fiskaly_org_${rowIndex}`}
                                            onPress={() => {
                                                if (!itr.integrationId) {
                                                    return;
                                                }
                                                navigate(
                                                    "FISKALY_ORGANIZATION",
                                                    { id: itr.integrationId }
                                                );
                                            }}
                                        >
                                            <DataTable.Cell
                                                textStyle={styles.rowHeader}
                                            >
                                                {itr.displayName}
                                            </DataTable.Cell>
                                            <DataTable.Cell>
                                                {itr.name}
                                            </DataTable.Cell>
                                            <DataTable.Cell>
                                                {itr.address}
                                            </DataTable.Cell>
                                            <DataTable.Cell
                                                style={styles.zipColumn}
                                            >
                                                {itr.zip}
                                            </DataTable.Cell>
                                            <DataTable.Cell>
                                                {itr.town}
                                            </DataTable.Cell>
                                            <DataTable.Cell
                                                style={styles.countryColumn}
                                            >
                                                {itr.countryCode}
                                            </DataTable.Cell>
                                            <DataTable.Cell
                                                style={styles.envColumn}
                                            >
                                                {itr.fiskalyEnvironment}
                                            </DataTable.Cell>
                                            <DataTable.Cell>
                                                {parentOrgName || null}
                                            </DataTable.Cell>
                                            <DataTable.Cell>
                                                {itr.integrationId &&
                                                itr.merchant
                                                    ? itr?.merchant.name
                                                    : null}
                                            </DataTable.Cell>
                                            <DataTable.Cell
                                                onPress={() =>
                                                    copyOnClick(itr.id)
                                                }
                                                textStyle={theme.styles.link}
                                            >
                                                {itr.id}
                                            </DataTable.Cell>
                                        </DataTable.Row>
                                    );
                                }
                            )
                        )}
                    </DataTable>
                )}
            </Surface>
        </AdminContainer>
    );
}

const styleFunc: StyleFunction = () => ({
    zipColumn: {
        flexBasis: 80,
        flexShrink: 0,
        flexGrow: 0,
    },
    countryColumn: {
        flexBasis: 80,
        flexShrink: 0,
        flexGrow: 0,
    },
    envColumn: {
        flexBasis: 60,
        flexShrink: 0,
        flexGrow: 0,
    },

    rowHeader: {
        fontWeight: "bold",
    },
});
